'use client';

import { useCallback, useEffect, useState } from 'react';
import { LogOut, UserPlus, Trash2, Edit2, Check, ChevronRight, Users } from 'lucide-react';
import {
  AmbientBackground,
  Button,
  IconActionButton,
  Badge,
  ErrorBanner,
  Field,
  TextInput,
  Toggle,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  SearchableMultiSelect,
  Spinner,
  PageHeader,
  HeroSection,
  PageFooter,
  PageShell,
  PageMain,
  LoadingState,
  DemoIcon,
  LanguageToggle,
} from '../ui';
import type { Demo, UserRecord } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';

export const UsersPage = ({ token, onBack, onLogout }: { token: string; onBack: () => void; onLogout: () => void }) => {
  const { t } = useLanguage();
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [demos, setDemos] = useState<Demo[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState<UserRecord | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [form, setForm] = useState({ email: '', name: '', password: '', is_admin: false, demo_ids: [] as number[] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [uRes, dRes] = await Promise.all([fetch('/api/users', { headers }), fetch('/api/demos', { headers })]);
      if (uRes.status === 401 || dRes.status === 401) {
        onLogout();
        return;
      }
      setUsers(await uRes.json());
      const demoData = await dRes.json();
      setDemos(Array.isArray(demoData) ? demoData : []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openCreate = () => {
    setEditUser(null);
    setForm({ email: '', name: '', password: '', is_admin: false, demo_ids: [] });
    setError('');
    setShowModal(true);
  };

  const openEdit = (u: UserRecord) => {
    setEditUser(u);
    setForm({ email: u.email, name: u.name || '', password: '', is_admin: u.is_admin, demo_ids: u.demo_ids });
    setError('');
    setShowModal(true);
  };

  const toggleDemo = (id: number) => {
    setForm((f) => ({
      ...f,
      demo_ids: f.demo_ids.includes(id) ? f.demo_ids.filter((d) => d !== id) : [...f.demo_ids, id],
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const body: any = { is_admin: form.is_admin, demo_ids: form.demo_ids, name: form.name };
      if (editUser) {
        if (form.password) body.password = form.password;
        const res = await fetch(`/api/users/${editUser.id}`, {
          method: 'PATCH',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const e = await res.json();
          throw new Error(e.detail || t('error.update'));
        }
      } else {
        if (!form.email || (!editUser && !form.password)) {
          throw new Error(t('error.emailRequired'));
        }
        const res = await fetch('/api/users', {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify(form),
        });
        if (!res.ok) {
          const e = await res.json();
          throw new Error(e.detail || t('error.create'));
        }
      }
      setShowModal(false);
      await loadData();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('error.unknown'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const res = await fetch(`/api/users/${id}`, { method: 'DELETE', headers });
      if (res.status === 401) {
        onLogout();
        return;
      }
      if (!res.ok) {
        const e = await res.json();
        alert(e.detail || t('error.delete'));
        return;
      }
      setDeleteConfirm(null);
      await loadData();
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <PageShell>
      <AmbientBackground scanline />

      <PageHeader
        navSlot={
          <Button variant="ghost-active" disabled className="px-3.5 py-1.5 text-[0.8rem]">
            <Users size={15} />
            <span>{t('hub.userManagementBtn')}</span>
          </Button>
        }
        actionsSlot={
          <>
            <LanguageToggle />
            <Button variant="ghost" onClick={onBack} className="px-3.5 py-1.5 text-[0.8rem]">
              <ChevronRight size={15} className="rotate-180" />
              <span>{t('users.backToHub')}</span>
            </Button>
            <Button variant="ghost" onClick={onLogout} aria-label={t('hub.logoutBtn')} className="px-3.5 py-1.5 text-[0.8rem]">
              <LogOut size={16} />
              <span>{t('hub.logoutBtn')}</span>
            </Button>
          </>
        }
      />

      <PageMain>
        <HeroSection
          className="mb-8"
          badge={t('users.heroBadge')}
          title={t('users.heroTitle1')}
          gradientTitle={t('users.heroTitle2')}
          description={t('users.heroDesc')}
        />

        <div className="mb-6 flex items-center justify-between gap-4">
          <div className="text-[0.85rem] text-white/35">
            {users.length} {users.length !== 1 ? t('users.usersCount') : t('users.userCount')}
          </div>
          <Button onClick={openCreate} id="up-create-user-btn">
            <UserPlus size={16} />
            {t('users.newUserBtn')}
          </Button>
        </div>

        {loading ? (
          <LoadingState label={t('users.loadingUsers')} />
        ) : (
          <div className="animate-card-in overflow-hidden rounded-2xl border border-brand-teal/12 bg-white/3">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-brand-teal/12">
                  <th className="px-5 py-3.5 text-left text-[0.72rem] font-semibold tracking-[0.06em] text-white/35 uppercase">{t('users.tableColUser')}</th>
                  <th className="px-5 py-3.5 text-left text-[0.72rem] font-semibold tracking-[0.06em] text-white/35 uppercase">{t('users.tableColRole')}</th>
                  <th className="px-5 py-3.5 text-left text-[0.72rem] font-semibold tracking-[0.06em] text-white/35 uppercase">{t('users.tableColAccess')}</th>
                  <th className="px-5 py-3.5 text-right text-[0.72rem] font-semibold tracking-[0.06em] text-white/35 uppercase">{t('users.tableColActions')}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-white/4 transition-colors duration-200 last:border-none hover:bg-brand-teal/3">
                    <td className="px-5 py-3.5 align-middle text-white/80">
                      <div className="flex items-center gap-3">
                        <div className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full border border-brand-teal/20 bg-linear-to-br from-brand-teal/20 to-brand-blue/20 text-[0.8rem] font-bold text-brand-teal">
                          {u.name ? u.name[0].toUpperCase() : u.email[0].toUpperCase()}
                        </div>
                        <div className="flex flex-col">
                          {u.name && <span className="text-[0.85rem] font-medium text-white/90">{u.name}</span>}
                          <span className={u.name ? "text-[0.75rem] text-white/50" : "text-[0.9rem]"}>{u.email}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3.5 align-middle text-white/80">
                      <Badge tone={u.is_admin ? 'teal' : 'neutral'}>{u.is_admin ? t('users.roleAdmin') : t('users.roleUser')}</Badge>
                    </td>
                    <td className="px-5 py-3.5 align-middle text-white/80">
                      <span className="text-[0.8rem] text-white/40">
                        {u.is_admin ? t('users.accessAll') : `${u.demo_ids.length} ${u.demo_ids.length !== 1 ? t('users.softwares') : t('users.software')}`}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 align-middle text-white/80">
                      <div className="flex items-center justify-end gap-1.5">
                        <IconActionButton onClick={() => openEdit(u)} title={t('users.actionEdit')}>
                          <Edit2 size={14} />
                        </IconActionButton>
                        {deleteConfirm === u.id ? (
                          <>
                            <IconActionButton tone="confirm" onClick={() => handleDelete(u.id)} title={t('users.actionConfirm')}>
                              <Check size={14} />
                            </IconActionButton>
                            <IconActionButton tone="cancel" onClick={() => setDeleteConfirm(null)} title={t('users.actionCancel')}>
                              <span className="text-sm leading-none">✕</span>
                            </IconActionButton>
                          </>
                        ) : (
                          <IconActionButton tone="danger" onClick={() => setDeleteConfirm(u.id)} title={t('users.actionDelete')}>
                            <Trash2 size={14} />
                          </IconActionButton>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <PageFooter />
      </PageMain>

      {showModal && (
        <Modal onClose={() => setShowModal(false)}>
          <ModalHeader title={editUser ? t('modal.editTitle') : t('modal.createTitle')} onClose={() => setShowModal(false)} />

          <ModalBody>
            {error && <ErrorBanner>{error}</ErrorBanner>}

            <Field label={t('login.emailLabel')}>
              <TextInput
                type="email"
                placeholder={t('login.emailPlaceholder')}
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                disabled={!!editUser}
              />
            </Field>

            <Field label={t('modal.nameLabel')}>
              <TextInput
                type="text"
                placeholder={t('modal.namePlaceholder')}
                value={form.name || ''}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </Field>

            <Field label={editUser ? t('modal.newPasswordLabel') : t('modal.passwordLabel')}>
              <TextInput
                type="password"
                placeholder={editUser ? '••••••••' : t('modal.passwordPlaceholder')}
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              />
            </Field>

            <div className="flex items-center justify-between gap-4 rounded-[0.65rem] border border-white/7 bg-white/3 px-4 py-3.5">
              <div>
                <div className="mb-0.5 text-[0.8rem] font-medium tracking-[0.02em] text-white/60">{t('modal.roleTitle')}</div>
                <div className="text-[0.75rem] text-white/35">{t('modal.roleDesc')}</div>
              </div>
              <Toggle
                checked={form.is_admin}
                onClick={() => setForm((f) => ({ ...f, is_admin: !f.is_admin, demo_ids: !f.is_admin ? [] : f.demo_ids }))}
              />
            </div>
            <div className="-mt-2 pl-1 text-[0.78rem] text-white/35">{form.is_admin ? t('modal.roleAdminLabel') : t('modal.roleUserLabel')}</div>

            {!form.is_admin && (
              <SearchableMultiSelect
                label={t('modal.accessLabel')}
                options={demos.map((d) => ({ id: Number(d.id), label: d.name, icon: <DemoIcon name={d.icon} /> }))}
                selectedIds={form.demo_ids}
                onToggle={toggleDemo}
                emptyLabel={t('modal.accessEmpty')}
                searchPlaceholder={t('modal.accessSearch')}
              />
            )}
          </ModalBody>

          <ModalFooter>
            <button
              className="cursor-pointer whitespace-nowrap rounded-[0.65rem] border border-white/10 bg-white/4 px-5 py-3 text-[0.85rem] text-white/45 transition-colors duration-200 hover:bg-white/7 hover:text-white"
              onClick={() => setShowModal(false)}
            >
              {t('modal.cancelBtn')}
            </button>
            <Button className="flex-1" onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Spinner size={15} />
                  {t('modal.saveLoading')}
                </>
              ) : (
                <>
                  <Check size={15} />
                  {editUser ? t('modal.saveChanges') : t('modal.createUser')}
                </>
              )}
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </PageShell>
  );
};
